import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { getCurrentUser, getPartnerProfile, updatePartnerProfileFields, updatePartnerProfile } from '../services/api';

const PartnerProfileForm = ({ onUpdate }) => {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState(null);
  const [fitnessCenter, setFitnessCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Form fields state
  const [formData, setFormData] = useState({
    country: '',
    city: '',
    address: '',
    postalCode: '',
    phone: '',
    website: '',
    hours: {
      monday: { open: '09:00', close: '22:00' },
      tuesday: { open: '09:00', close: '22:00' },
      wednesday: { open: '09:00', close: '22:00' },
      thursday: { open: '09:00', close: '22:00' },
      friday: { open: '09:00', close: '22:00' },
      saturday: { open: '10:00', close: '20:00' },
      sunday: { open: '10:00', close: '18:00' }
    },
    services: [],
    mainPhoto: '',
    otherPhotos: []
  });
  
  // Services options
  const serviceOptions = [
    'Gym', 'CrossFit', 'Yoga', 'Pilates', 'Pool', 'Sauna',
    'Cardio', 'Functional', 'Weights', 'Boxing'
  ];
  
  useEffect(() => {
    loadUserData();
  }, []);
  
  const loadUserData = async () => {
    setLoading(true);
    const user = getCurrentUser();
    if (!user || user.role !== 'partner') {
      setSnackbar({
        open: true,
        message: t('profile.notPartner', 'You are not logged in as a partner'),
        severity: 'error'
      });
      setLoading(false);
      return;
    }
    
    setCurrentUser(user);
    
    try {
      console.log('Getting profile for partner ID:', user.id);
      const response = await getPartnerProfile(user.id);
      console.log('Partner profile response:', response);
      
      if (response.status === 'success' && response.data) {
        // Create default form data with partner info
        let newFormData = { ...formData };
        
        // If partner has fitness centers
        if (response.data.fitnessCenters && response.data.fitnessCenters.length > 0) {
          const center = response.data.fitnessCenters[0];
          console.log('Fitness center data:', center);
          setFitnessCenter(center);
          
          // Format hours data properly
          let hours = center.hours || formData.hours;
          if (typeof hours === 'string') {
            try {
              hours = JSON.parse(hours);
            } catch (e) {
              console.error('Error parsing hours JSON:', e);
              hours = formData.hours;
            }
          }
          
          // Format services array properly
          let services = center.services || [];
          if (typeof services === 'string') {
            try {
              services = JSON.parse(services);
            } catch (e) {
              console.error('Error parsing services JSON:', e);
              services = [];
            }
          }
          
          // Format otherPhotos array properly
          let otherPhotos = center.other_photos || [];
          if (typeof otherPhotos === 'string') {
            try {
              otherPhotos = JSON.parse(otherPhotos);
            } catch (e) {
              console.error('Error parsing other_photos JSON:', e);
              otherPhotos = [];
            }
          }
          
          // Ensure we have the default structure for hours if it's missing
          const defaultHours = {
            monday: { open: '09:00', close: '22:00' },
            tuesday: { open: '09:00', close: '22:00' },
            wednesday: { open: '09:00', close: '22:00' },
            thursday: { open: '09:00', close: '22:00' },
            friday: { open: '09:00', close: '22:00' },
            saturday: { open: '10:00', close: '20:00' },
            sunday: { open: '10:00', close: '18:00' }
          };
          
          // Make sure all days are present in hours
          for (const day in defaultHours) {
            if (!hours[day]) {
              hours[day] = defaultHours[day];
            }
          }
          
          console.log('Processed hours:', hours);
          console.log('Processed services:', services);
          
          newFormData = {
            country: center.country || '',
            city: center.city || '',
            address: center.address || '',
            postalCode: center.postal_code || '',
            phone: center.phone || '',
            website: center.website || '',
            hours: hours,
            services: services,
            mainPhoto: center.main_photo || '',
            otherPhotos: otherPhotos
          };
        } else {
          // No fitness centers found or associated with partner
          console.log('No fitness centers found for partner, creating a new one');
          
          // Display warning to user
          setSnackbar({
            open: true,
            message: t('profile.noFitnessCenter', 'No fitness center found for your partner account. You can create one now.'),
            severity: 'info'
          });
          
          // Create a dummy fitness center just to allow editing
          setFitnessCenter({
            id: 'new',
            name: response.data.partnername || 'New Fitness Center',
            partner_id: user.id
          });
        }
        
        // Set form data
        setFormData(newFormData);
      } else {
        console.error('Error in profile response:', response);
        setSnackbar({
          open: true,
          message: t('profile.errorLoading', 'Error loading profile data'),
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading partner profile:', error);
      setSnackbar({
        open: true,
        message: t('profile.errorLoading', 'Error loading profile data'),
        severity: 'error'
      });
    }
    
    setLoading(false);
  };
  
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleHoursChange = (day, field, value) => {
    setFormData({
      ...formData,
      hours: {
        ...formData.hours,
        [day]: {
          ...formData.hours[day],
          [field]: value
        }
      }
    });
  };
  
  const handleServiceToggle = (service) => {
    const currentServices = [...formData.services];
    const serviceIndex = currentServices.indexOf(service);
    
    if (serviceIndex === -1) {
      // Add service
      setFormData({
        ...formData,
        services: [...currentServices, service]
      });
    } else {
      // Remove service
      currentServices.splice(serviceIndex, 1);
      setFormData({
        ...formData,
        services: currentServices
      });
    }
  };
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!currentUser) return;
    
    setSubmitting(true);
    
    try {
      let result;
      
      // If we have a fitness center, update it
      if (fitnessCenter && fitnessCenter.id !== 'new') {
        console.log('Updating existing fitness center:', fitnessCenter.id);
        result = await updatePartnerProfileFields(
          currentUser.id,
          fitnessCenter.id,
          {
            country: formData.country,
            city: formData.city,
            address: formData.address,
            postalCode: formData.postalCode,
            phone: formData.phone,
            website: formData.website,
            hours: formData.hours,
            services: formData.services,
            mainPhoto: formData.mainPhoto,
            otherPhotos: formData.otherPhotos
          }
        );
      } else {
        // If we don't have a fitness center, create one
        console.log('Creating new fitness center for partner:', currentUser.id);
        result = await updatePartnerProfile(currentUser.id, {
          name: currentUser.partnerName || 'Fitness Center',
          address: formData.address || 'Address required',
          latitude: 0,
          longitude: 0,
          phone: formData.phone,
          website: formData.website,
          mainPhoto: formData.mainPhoto,
          otherPhotos: formData.otherPhotos,
          city: formData.city,
          country: formData.country,
          postalCode: formData.postalCode,
          type: formData.services[0] || 'Gym',
          hours: formData.hours,
          services: formData.services
        });
      }
      
      if (result.status === 'success') {
        setSnackbar({
          open: true,
          message: fitnessCenter && fitnessCenter.id !== 'new' 
            ? t('profile.updateSuccess', 'Fitness center updated successfully')
            : t('profile.createSuccess', 'Fitness center created successfully'),
          severity: 'success'
        });
        
        // If a new fitness center was created, we need to reload the data
        if (!fitnessCenter || fitnessCenter.id === 'new') {
          await loadUserData();
        }
        
        if (onUpdate) onUpdate(result.data);
      } else {
        setSnackbar({
          open: true,
          message: result.message || t('profile.updateError', 'Error updating fitness center'),
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating fitness center:', error);
      setSnackbar({
        open: true,
        message: t('profile.updateError', 'Error updating fitness center'),
        severity: 'error'
      });
    }
    
    setSubmitting(false);
  };
  
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        {fitnessCenter && fitnessCenter.id !== 'new'
          ? t('profile.editTitle', 'Edit Your Fitness Center')
          : t('profile.createTitle', 'Create Your Fitness Center')}
      </Typography>
      
      {fitnessCenter && fitnessCenter.id !== 'new' && (
        <Typography variant="subtitle1" color="primary" gutterBottom>
          {fitnessCenter.name}
        </Typography>
      )}
      
      <Divider sx={{ mb: 3 }} />
      
      {fitnessCenter && fitnessCenter.id === 'new' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('profile.createInfo', 'You don\'t have a fitness center associated with your partner account yet. Fill in the details below to create one.')}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Location Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t('profile.locationInfo', 'Location Information')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="country"
              label={t('profile.country', 'Country')}
              fullWidth
              value={formData.country}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="city"
              label={t('profile.city', 'City')}
              fullWidth
              value={formData.city}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={8}>
            <TextField
              name="address"
              label={t('profile.address', 'Address')}
              fullWidth
              value={formData.address}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              name="postalCode"
              label={t('profile.postalCode', 'Postal Code')}
              fullWidth
              value={formData.postalCode}
              onChange={handleInputChange}
            />
          </Grid>
          
          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {t('profile.contactInfo', 'Contact Information')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="phone"
              label={t('profile.phone', 'Phone')}
              fullWidth
              value={formData.phone}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="website"
              label={t('profile.website', 'Website')}
              fullWidth
              value={formData.website}
              onChange={handleInputChange}
            />
          </Grid>
          
          {/* Operating Hours */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {t('profile.hours', 'Operating Hours')}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                <Grid item key={day} xs={6} sm={4} md={3} lg={true}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                      {t(`profile.days.${day}`, day.charAt(0).toUpperCase() + day.slice(1))}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        type="time"
                        label={t('profile.openTime', 'Open')}
                        value={formData.hours[day]?.open || '09:00'}
                        onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { fontSize: '1rem' } }}
                      />
                      <TextField
                        fullWidth
                        type="time"
                        label={t('profile.closeTime', 'Close')}
                        value={formData.hours[day]?.close || '22:00'}
                        onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { fontSize: '1rem' } }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
          
          {/* Services */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {t('profile.services', 'Services')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {serviceOptions.map(service => {
                const isSelected = Array.isArray(formData.services) && 
                                  formData.services.some(s => 
                                    typeof s === 'string' && 
                                    s.toLowerCase() === service.toLowerCase());
                return (
                  <Chip
                    key={service}
                    label={service}
                    onClick={() => handleServiceToggle(service)}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{ 
                      m: 0.5, 
                      fontWeight: isSelected ? 'bold' : 'normal',
                      bgcolor: isSelected ? 'primary.main' : 'transparent',
                      color: isSelected ? 'white' : 'inherit',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Grid>
          
          {/* Photos */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {t('profile.photos', 'Photos')}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="mainPhoto"
              label={t('profile.mainPhoto', 'Main Photo URL')}
              fullWidth
              value={formData.mainPhoto}
              onChange={handleInputChange}
              placeholder="https://example.com/your-photo.jpg"
              helperText={t('profile.photoHelp', 'Enter the URL of your main photo')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="otherPhotos"
              label={t('profile.otherPhotos', 'Other Photos (comma separated URLs)')}
              fullWidth
              value={Array.isArray(formData.otherPhotos) ? formData.otherPhotos.join(', ') : ''}
              onChange={(e) => {
                const urls = e.target.value.split(',').map(url => url.trim()).filter(url => url);
                setFormData({
                  ...formData,
                  otherPhotos: urls
                });
              }}
              placeholder="https://example.com/photo1.jpg, https://example.com/photo2.jpg"
              helperText={t('profile.otherPhotosHelp', 'Enter comma-separated URLs of additional photos')}
            />
          </Grid>
          
          {/* Submit button */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={submitting}
              sx={{ minWidth: 200 }}
            >
              {submitting ? (
                <CircularProgress size={24} />
              ) : (
                t('profile.saveChanges', 'Save Changes')
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PartnerProfileForm; 